import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, providerApi, type CashDrawerSummaryRecord } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";

// Every screen showing the shift reloads when any of them changes it.
const listeners = new Set<() => void>();
export function notifyShiftChanged() {
  listeners.forEach((listener) => listener());
}

export function subscribeShiftChanged(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * The venue's cash shift (a cash drawer session on the API). A shift is only
 * about the cash in the till; it never changes whether the venue is open.
 */
export function useCashShift(venueId: string | null | undefined) {
  const { t } = useAppPreferences();
  const [summary, setSummary] = useState<CashDrawerSummaryRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    if (!venueId) {
      setSummary(null);
      return null;
    }
    const requestId = ++requestRef.current;
    setIsLoading(true);
    try {
      const next = await providerApi.getCashDrawerSummary(venueId);
      if (requestId === requestRef.current) {
        setSummary(next);
        setError(null);
      }
      return next;
    } catch (requestError) {
      if (requestId === requestRef.current) {
        setError(requestError instanceof ApiError && requestError.status !== 0 ? requestError.detail : t("Could not load the shift."));
      }
      return null;
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, [t, venueId]);

  useEffect(() => {
    setSummary(null);
    void reload();
  }, [reload]);

  useEffect(() => {
    const listener = () => void reload();
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, [reload]);

  const openShift = summary?.venue_id === venueId ? summary?.open_drawer ?? null : null;
  return {
    summary,
    openShift,
    lastShift: summary?.last_closed_drawer ?? null,
    /** Cash that should be in the till right now (starting cash + cash taken this shift). */
    expectedCash: openShift ? summary?.expected_cash_amount ?? 0 : 0,
    cashTaken: openShift ? summary?.payment_totals.cash ?? 0 : 0,
    currency: summary?.currency ?? "EGP",
    isLoading,
    error,
    reload,
  };
}
